import { motion } from "framer-motion";
import { MapPin, CalendarDays, Store, Gift, Utensils, ExternalLink } from "lucide-react";

const domaines = [
  { nom: "Domaine Mazille", adresse: "23 rue du Pavé, 71390 Bissey-sous-Cruchaud", site: "https://www.domainemazille.com" },
  { nom: "Domaine des Chauchoux", adresse: "71150 Rully", site: "https://domainedeschauchoux.com" },
  { nom: "Domaine Berthenet", adresse: "2 rue du Lavoir, 71390 Montagny-lès-Buxy", site: "https://www.vinsberthenet.com" },
  { nom: "Domaine La Renarde", adresse: "Lieu-dit La Renarde, 71390 Jully-lès-Buxy", site: "https://domainelarenarde.com" },
  { nom: "Domaine Lagarde", adresse: "71390 Rosey", site: "https://domaine-lagarde.fr" },
  { nom: "Cave de Bissey-sous-Cruchaud", adresse: "14 rue des Millerands, 71390 Bissey-sous-Cruchaud", site: "https://www.cave-bissey.com" },
  { nom: "Maison Millebuis — Buxy & Saint-Gengoux", adresse: "4-6 route de Chalon, 71390 Buxy", site: "https://www.millebuis.fr" },
  { nom: "Cave coopérative de Genouilly", adresse: "2 Allée du 19 Mars 1962, 71460 Genouilly", site: "https://vigneronsdegenouilly.fr" },
];

const evenements = [
  { nom: "Saint Vincent Tournante", lieu: "Bourgogne" },
  { nom: "Salon Vinessens", lieu: "Ouroux-sur-Saône" },
  { nom: "Festibière", lieu: "Chalon-sur-Saône" },
  { nom: "Fête des Lumières", lieu: "Mont Saint-Vincent" },
  { nom: "Fête du graton", lieu: "Montceau-les-Mines" },
  { nom: "Marchés festifs de La Treche", lieu: "Sanvignes-les-Mines" },
  { nom: "Fête de l'automne", lieu: "Saint-Bérain-sur-Dheune" },
  { nom: "Marché de Noël", lieu: "Couches" },
];

const revendeurs = [
  { type: "Épicerie", nom: "L'Art de vivre", lieu: "2c route de Lyon, 71100 Lux" },
];

const restaurants = [
  { nom: "L'Étape Charollaise", adresse: "1 Route de Montceau, 71460 Saint-Marcelin-de-Cray", site: "https://www.restaurant-etape-charollaise.fr" },
  { nom: "Le Salon de Grégoire", adresse: "5 rue de la République, 71640 Givry", site: "https://restaurantlesalondegregoire.com" },
];

const OuNousTrouver = () => {
  return (
    <section data-testid="trouver-section" id="trouver" className="relative py-24 lg:py-32 bg-bordeaux text-white overflow-hidden">
      <div className="absolute inset-0 grain-overlay" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <span className="text-sm uppercase tracking-[0.3em] text-creme font-500">Allons à votre rencontre</span>
          <h2 className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight">Où nous trouver</h2>
          <p className="text-lg text-white/80 mt-5 leading-relaxed">
            Fidèles à l'esprit du circuit court, nous cultivons le contact direct et la convivialité.
            Retrouvez-nous lors des portes ouvertes de nos domaines partenaires et sur nos rendez-vous gourmands.
            Marchés festifs, fêtes médiévales, apéros géants… c'est là que nous nous sentons pleinement
            dans notre élément : on adore l'ambiance, la fête et le partage !
          </p>
        </div>

        <motion.div
          data-testid="boutique-ferme"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-10 rounded-2xl bg-creme text-foreground p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5"
        >
          <div className="w-14 h-14 rounded-full bg-bordeaux text-white flex items-center justify-center shrink-0">
            <Store size={26} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-2xl text-bordeaux">Notre boutique, ouverte toute l'année</h3>
            <p className="text-foreground/75 mt-1 leading-relaxed">
              Venez retrouver toutes nos préparations directement sur place, à Savianges.
              <strong className="text-foreground"> Merci de nous appeler avant de venir</strong> afin que nous vous accueillions dans les meilleures conditions.
            </p>
          </div>
          <a
            href="tel:0662125192"
            data-testid="boutique-tel"
            className="rounded-full bg-bordeaux text-white font-600 px-6 py-3 whitespace-nowrap hover:bg-bordeaux-dark transition-colors text-center"
          >
            06 62 12 51 92
          </a>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mt-14">
          <motion.div
            data-testid="portes-ouvertes-block"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl bg-white/8 border border-white/15 p-8 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3 text-creme">
              <MapPin size={22} />
              <h3 className="font-heading text-2xl">Portes ouvertes des domaines & caves</h3>
            </div>
            <ul className="mt-6 divide-y divide-white/10">
              {domaines.map((d, i) => (
                <li key={d.nom} data-testid={`domaine-${i}`} className="py-4 first:pt-0">
                  <div className="font-600 text-white">{d.nom}</div>
                  {d.adresse && <div className="text-sm text-white/70 mt-0.5">{d.adresse}</div>}
                  {d.site && (
                    <a href={d.site} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-creme hover:text-white mt-1 transition-colors">
                      Site internet <ExternalLink size={13} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            data-testid="evenements-block"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl bg-white/8 border border-white/15 p-8 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3 text-creme">
              <CalendarDays size={22} />
              <h3 className="font-heading text-2xl">Salons & événements gourmands</h3>
            </div>
            <ul className="mt-6 divide-y divide-white/10">
              {evenements.map((e, i) => (
                <li key={`${e.nom}-${e.lieu}`} data-testid={`event-${i}`} className="py-4 first:pt-0 flex items-baseline justify-between gap-4">
                  <span className="font-600 text-white">{e.nom}</span>
                  <span className="text-sm text-white/70 whitespace-nowrap">{e.lieu}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-white/60">
              … et tant d'autres ! Sans oublier la vente directe sur place et nos portes ouvertes à Savianges.
            </p>
          </motion.div>

          <motion.div
            data-testid="revendeurs-block"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="rounded-2xl bg-white/8 border border-white/15 p-8 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3 text-creme">
              <Store size={22} />
              <h3 className="font-heading text-2xl">Nos revendeurs</h3>
            </div>
            <ul className="mt-6 divide-y divide-white/10">
              {revendeurs.map((r, i) => (
                <li key={r.nom} data-testid={`revendeur-${i}`} className="py-4 first:pt-0">
                  <div className="text-xs uppercase tracking-[0.2em] text-creme/80">{r.type}</div>
                  <div className="font-600 text-white mt-1">{r.nom}</div>
                  <div className="text-sm text-white/70 mt-0.5">{r.lieu}</div>
                  {r.site && (
                    <a href={r.site} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-creme hover:text-white mt-1 transition-colors">
                      Voir la page <ExternalLink size={13} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            data-testid="restaurants-block"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="rounded-2xl bg-white/8 border border-white/15 p-8 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3 text-creme">
              <Utensils size={22} />
              <h3 className="font-heading text-2xl">Nos restaurants partenaires</h3>
            </div>
            <ul className="mt-6 divide-y divide-white/10">
              {restaurants.map((r, i) => (
                <li key={r.nom} data-testid={`restaurant-${i}`} className="py-4 first:pt-0">
                  <div className="font-600 text-white">{r.nom}</div>
                  <div className="text-sm text-white/70 mt-0.5">{r.adresse}</div>
                  {r.site && (
                    <a href={r.site} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-creme hover:text-white mt-1 transition-colors">
                      Site internet <ExternalLink size={13} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          data-testid="ventes-noel"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-8 rounded-2xl bg-creme text-foreground p-6 sm:p-7 flex items-center gap-4"
        >
          <Gift size={26} className="text-bordeaux shrink-0" />
          <p className="leading-relaxed">
            <strong className="text-bordeaux">Ventes de Noël :</strong> nous travaillons avec les
            associations de parents d'élèves et les comités d'entreprise pour vos commandes de fin d'année.
            Contactez-nous pour que nous étudiions ensemble votre besoin : nous vous fournissons des
            <strong className="text-foreground"> bons de commande personnalisés</strong>.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default OuNousTrouver;
